    import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Layout } from "react-grid-layout";

import useShallowSelector from "./useShalowSelector";

import { addFieldRequest, deleteFieldRequest, duplicateFieldRequest } from "./../store/fields/actionCreators";
import { changeCurrentField } from "./../store/ui/actionCreators";

import { selectUICurrentField } from "./../store/ui/selectors";
import { selectFields } from "./../store/fields/selectors";

import {
    IField,
    CurrentField,
    FieldTypes,
    IDuplicateFieldRequestData,
    IDeleteFieldRequestData,
    IBoomformField,
} from "./../store/fields/types";
import { IPage } from "./../store/pages/types";

type IOnFieldDuplicateData = IDuplicateFieldRequestData;

export interface onAdd {
    (type: FieldTypes, pageId: IPage["_id"], layout: Layout | null, data?: Partial<IBoomformField & IField>): void;
}

export interface onCurrentFieldSelect {
    (newCurrentField: CurrentField): void;
}

export interface onFieldDuplicate {
    ({ pageId, fieldId, index, pageFieldsIds, fieldType, options }: IOnFieldDuplicateData): void;
}

export interface onFIeldDelete {
    ({ pageId, fieldId }: IDeleteFieldRequestData): void;
}

type UseFields = () => {
    onAdd: onAdd;
    fields: IField[];
    current: CurrentField;
    onCurrentFieldSelect: onCurrentFieldSelect;
    onFieldDuplicate: onFieldDuplicate;
    onFieldDelete: onFIeldDelete;
};

const useFields: UseFields = () => {
    const dispatch = useDispatch();

    const fields = useShallowSelector(selectFields);
    const currentField = useShallowSelector(selectUICurrentField);

    const onAdd = useCallback(
        (type, pageId, layout, data) => {
            dispatch(
                addFieldRequest({
                    type,
                    pageId,
                    layout,
                    data,
                }),
            );
        },
        [dispatch],
    );

    const onCurrentFieldSelect: onCurrentFieldSelect = useCallback(
        newCurrentField => {
            dispatch(changeCurrentField(newCurrentField));
        },
        [dispatch],
    );

    const onFieldDuplicate: onFieldDuplicate = useCallback(
        ({ pageId, fieldId, index, pageFieldsIds, fieldType, options }) => {
            dispatch(duplicateFieldRequest({ pageId, fieldId, index, pageFieldsIds, fieldType, options }));
        },
        [dispatch],
    );

    const onFieldDelete: onFIeldDelete = useCallback(
        ({ pageId, fieldId }) => {
            dispatch(deleteFieldRequest({ pageId, fieldId }));
        },
        [dispatch],
    );

    return {
        fields,
        onAdd,
        current: currentField,
        onCurrentFieldSelect,
        onFieldDuplicate,
        onFieldDelete,
    };
};

export default useFields;
